/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IActionContext } from '@msdyn365-commerce/core';
import {
    CheckoutModule,
    ErrorCode,
    ErrorLocation,
    ErrorMessageResource,
    getCheckoutState,
    ICheckoutError,
    ICheckoutState
} from '@msdyn365-commerce/global-state/';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class -- create global class
export class CheckoutErrors {
    public static readonly ActionError: ICheckoutError = {
        errorCode: ErrorCode.ActionError,
        errorMessageResource: ErrorMessageResource.checkoutTimeoutErrorMessage,
        errorLocation: ErrorLocation.Checkout
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_BlockedLoyaltyCard: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_BlockedLoyaltyCard,
        errorMessageResource: ErrorMessageResource.blockedLoyaltyCardErrorMessage,
        errorLocation: ErrorLocation.CheckoutLoyalty
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_CustomerAccountLimitSignDifferentFromAmountDue: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_CustomerAccountLimitSignDifferentFromAmountDue,
        errorMessageResource: ErrorMessageResource.customerAccountLimitSignDifferentFromAmountDueErrorMessage,
        errorLocation: ErrorLocation.CheckoutCustomerAccountPayment
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_CustomerAccountPaymentExceedsCustomerAccountFloorLimit: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_CustomerAccountPaymentExceedsCustomerAccountFloorLimit,
        errorMessageResource: ErrorMessageResource.customerAccountPaymentExceedsCustomerAccountFloorLimitErrorMessage,
        errorLocation: ErrorLocation.CheckoutCustomerAccountPayment
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_CustomerAccountPaymentExceedsTotalAmountForCarryOutAndReturnItems: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_CustomerAccountPaymentExceedsTotalAmountForCarryOutAndReturnItems,
        errorMessageResource: ErrorMessageResource.customerAccountPaymentExceedsTotalAmountForCarryOutAndReturnItemsErrorMessage,
        errorLocation: ErrorLocation.CheckoutCustomerAccountPayment
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_CustomerAccountPaymentForCustomerWithoutAllowOnAccount: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_CustomerAccountPaymentForCustomerWithoutAllowOnAccount,
        errorMessageResource: ErrorMessageResource.customerAccountPaymentForCustomerWithoutAllowOnAccountErrorMessage,
        errorLocation: ErrorLocation.CheckoutCustomerAccountPayment
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_CustomerAccountPaymentNotAllowedForOpenInvoices: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_CustomerAccountPaymentNotAllowedForOpenInvoices,
        errorMessageResource: ErrorMessageResource.customerAccountPaymentNotAllowedForOpenInvoicesErrorMessage,
        errorLocation: ErrorLocation.CheckoutCustomerAccountPayment
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_FraudRisk: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_FraudRisk,
        errorMessageResource: ErrorMessageResource.fraudRiskErrorMessage,
        errorLocation: ErrorLocation.CheckoutPaymentInstrument
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_GiftCardBalanceInquiryFailed: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_GiftCardBalanceInquiryFailed,
        errorMessageResource: ErrorMessageResource.giftCardBalanceInquiryFailedErrorMessage,
        errorLocation: ErrorLocation.CheckoutGiftCard
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_GiftCardCurrencyMismatch: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_GiftCardCurrencyMismatch,
        errorMessageResource: ErrorMessageResource.giftCardCurrencyMismatchErrorMessage,
        errorLocation: ErrorLocation.CheckoutGiftCard
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_InvalidCartVersion: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_InvalidCartVersion,
        errorMessageResource: ErrorMessageResource.invalidCartVersionErrorMessage,
        errorLocation: ErrorLocation.Checkout
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_InvalidLoyaltyCardNumber: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_InvalidLoyaltyCardNumber,
        errorMessageResource: ErrorMessageResource.invalidLoyaltyCardNumberErrorMessage,
        errorLocation: ErrorLocation.CheckoutLoyalty
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_InvalidPaymentRequest: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_InvalidPaymentRequest,
        errorMessageResource: ErrorMessageResource.invalidPaymentRequestErrorMessage,
        errorLocation: ErrorLocation.CheckoutPaymentInstrument
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_InvalidPaymentRequest_Console: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_InvalidPaymentRequest,
        errorMessageResource: ErrorMessageResource.invalidPaymentRequestConsoleErrorMessage,
        errorLocation: ErrorLocation.Console
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_MissingRequiredCartTenderLines: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_MissingRequiredCartTenderLines,
        errorMessageResource: ErrorMessageResource.missingRequiredCartTenderLinesErrorMessage,
        errorLocation: ErrorLocation.CheckoutPaymentInstrument
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_MultipleCustomerAccountPaymentsNotAllowed: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_MultipleCustomerAccountPaymentsNotAllowed,
        errorMessageResource: ErrorMessageResource.multipleCustomerAccountPaymentsNotAllowedErrorMessage,
        errorLocation: ErrorLocation.CheckoutCustomerAccountPayment
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_NoMoreThanOneLoyaltyTender: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_NoMoreThanOneLoyaltyTender,
        errorMessageResource: ErrorMessageResource.noMoreThanOneLoyaltyTenderErrorMessage,
        errorLocation: ErrorLocation.CheckoutLoyalty
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_NoResponseFromGatewayForGiftCardBalanceInquiry: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_NoResponseFromGatewayForGiftCardBalanceInquiry,
        errorMessageResource: ErrorMessageResource.noResponseFromGatewayForGiftCardBalanceInquiryErrorMessage,
        errorLocation: ErrorLocation.CheckoutGiftCard
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_NoTenderLoyaltyCard: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_NoTenderLoyaltyCard,
        errorMessageResource: ErrorMessageResource.noTenderLoyaltyCardErrorMessage,
        errorLocation: ErrorLocation.CheckoutLoyalty
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_NotEnoughRewardPoints: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_NotEnoughRewardPoints,
        errorMessageResource: ErrorMessageResource.notEnoughRewardPointsErrorMessage,
        errorLocation: ErrorLocation.CheckoutLoyalty
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_PaymentAlreadyVoided: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_PaymentAlreadyVoided,
        errorMessageResource: ErrorMessageResource.paymentAlreadyVoidedErrorMessage,
        errorLocation: ErrorLocation.CheckoutPaymentInstrument
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_PaymentAmountExceedsGiftBalance: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_PaymentAmountExceedsGiftBalance,
        errorMessageResource: ErrorMessageResource.paymentAmountExceedsGiftBalanceErrorMessage,
        errorLocation: ErrorLocation.CheckoutGiftCard
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_PaymentRequiresMerchantProperties: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_PaymentRequiresMerchantProperties,
        errorMessageResource: ErrorMessageResource.paymentRequiresMerchantPropertiesErrorMessage,
        errorLocation: ErrorLocation.CheckoutPaymentInstrument
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_PaymentRequiresMerchantProperties_Console: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_PaymentRequiresMerchantProperties,
        errorMessageResource: ErrorMessageResource.paymentRequiresMerchantPropertiesConsoleErrorMessage,
        errorLocation: ErrorLocation.Console
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_PaymentUsingUnauthorizedAccount: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_PaymentUsingUnauthorizedAccount,
        errorMessageResource: ErrorMessageResource.paymentUsingUnauthorizedAccountErrorMessage,
        errorLocation: ErrorLocation.CheckoutCustomerAccountPayment
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_RefundAmountMoreThanAllowed: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_RefundAmountMoreThanAllowed,
        errorMessageResource: ErrorMessageResource.refundAmountMoreThanAllowedErrorMessage,
        errorLocation: ErrorLocation.CheckoutLoyalty
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_UnableToAuthorizePayment: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_UnableToAuthorizePayment,
        errorMessageResource: ErrorMessageResource.unableToAuthorizePaymentErrorMessage,
        errorLocation: ErrorLocation.CheckoutPaymentInstrument
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_UnableToAuthorizePaymentCardAdditionalContextRequired: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_UnableToAuthorizePaymentCardAdditionalContextRequired,
        errorMessageResource: ErrorMessageResource.unableToAuthorizePaymentCardAdditionalContextRequiredErrorMessage,
        errorLocation: ErrorLocation.CheckoutPaymentInstrument
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_UnableToAuthorizePaymentCardTypeMissingOrNotSupported: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_UnableToAuthorizePaymentCardTypeMissingOrNotSupported,
        errorMessageResource: ErrorMessageResource.unableToAuthorizePaymentCardTypeMissingOrNotSupportedErrorMessage,
        errorLocation: ErrorLocation.CheckoutPaymentInstrument
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_UnableToCancelPayment: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_UnableToCancelPayment,
        errorMessageResource: ErrorMessageResource.unableToCancelPaymentErrorMessage,
        errorLocation: ErrorLocation.CheckoutPaymentInstrument
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_UnableToReadCardTokenInfo: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_UnableToReadCardTokenInfo,
        errorMessageResource: ErrorMessageResource.unableToReadCardTokenInfoErrorMessage,
        errorLocation: ErrorLocation.CheckoutPaymentInstrument
    };

    public static readonly Microsoft_Dynamics_Commerce_Runtime_UnableToRetrieveCardPaymentAcceptResult: ICheckoutError = {
        errorCode: ErrorCode.Microsoft_Dynamics_Commerce_Runtime_UnableToRetrieveCardPaymentAcceptResult,
        errorMessageResource: ErrorMessageResource.unableToRetrieveCardPaymentAcceptResultErrorMessage,
        errorLocation: ErrorLocation.CheckoutPaymentInstrument
    };

    public static readonly checkoutErrorsList: ICheckoutError[] = [
        CheckoutErrors.ActionError,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_BlockedLoyaltyCard,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_CustomerAccountLimitSignDifferentFromAmountDue,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_CustomerAccountPaymentExceedsCustomerAccountFloorLimit,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_CustomerAccountPaymentExceedsTotalAmountForCarryOutAndReturnItems,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_CustomerAccountPaymentForCustomerWithoutAllowOnAccount,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_CustomerAccountPaymentNotAllowedForOpenInvoices,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_FraudRisk,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_GiftCardBalanceInquiryFailed,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_GiftCardCurrencyMismatch,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_InvalidCartVersion,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_InvalidLoyaltyCardNumber,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_InvalidPaymentRequest,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_InvalidPaymentRequest_Console,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_MissingRequiredCartTenderLines,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_MultipleCustomerAccountPaymentsNotAllowed,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_NoMoreThanOneLoyaltyTender,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_NoResponseFromGatewayForGiftCardBalanceInquiry,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_NoTenderLoyaltyCard,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_NotEnoughRewardPoints,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_PaymentAlreadyVoided,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_PaymentAmountExceedsGiftBalance,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_PaymentRequiresMerchantProperties,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_PaymentRequiresMerchantProperties_Console,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_PaymentUsingUnauthorizedAccount,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_RefundAmountMoreThanAllowed,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_UnableToAuthorizePayment,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_UnableToAuthorizePaymentCardAdditionalContextRequired,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_UnableToAuthorizePaymentCardTypeMissingOrNotSupported,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_UnableToCancelPayment,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_UnableToReadCardTokenInfo,
        CheckoutErrors.Microsoft_Dynamics_Commerce_Runtime_UnableToRetrieveCardPaymentAcceptResult
    ];
}

export async function setCheckoutErrors(
    errorCode: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resources: any,
    isExpressCheckoutApplied: boolean,
    ctx: IActionContext
): Promise<void> {
    let clientCheckoutError: ICheckoutError | undefined;
    let consoleCheckoutErrors: ICheckoutError[] = [];

    const checkoutState: ICheckoutState = await getCheckoutState(ctx).catch((error: Error) => {
        ctx.telemetry.exception(error);
        throw error;
    });

    // find the checkout errors
    for (const checkoutError of CheckoutErrors.checkoutErrorsList) {
        if (errorCode === checkoutError.errorCode) {
            if (checkoutError.errorLocation === ErrorLocation.Console) {
                let consoleCheckoutError: ICheckoutError = {
                    errorCode: checkoutError.errorCode,
                    errorMessageResource: checkoutError.errorMessageResource,
                    errorLocation: checkoutError.errorLocation
                };
                consoleCheckoutErrors.push(consoleCheckoutError);
            } else {
                if (clientCheckoutError) {
                    ctx.telemetry.exception(new Error('Invalid number of client checkout errors.'));
                    throw new Error('Invalid number of client checkout errors.');
                } else {
                    clientCheckoutError = {
                        errorCode: checkoutError.errorCode,
                        errorMessageResource: checkoutError.errorMessageResource,
                        errorLocation: checkoutError.errorLocation
                    };
                }
            }
        }
    }

    // log the console checkout errors
    for (let consoleCheckoutError of consoleCheckoutErrors) {
        consoleCheckoutError.errorMessage = getCheckoutErrorMessage(resources, consoleCheckoutError.errorMessageResource!);
        console.log(consoleCheckoutError.errorMessage);
    }

    // handle the client checkout error
    if (clientCheckoutError) {
        clientCheckoutError.errorMessage = getCheckoutErrorMessage(resources, clientCheckoutError.errorMessageResource!);

        if (isExpressCheckoutApplied && clientCheckoutError.errorLocation === ErrorLocation.CheckoutPaymentInstrument) {
            clientCheckoutError.errorLocation = ErrorLocation.Checkout;
        }

        await checkoutState.updateCheckoutError({ newCheckoutError: clientCheckoutError });
        await checkoutState.updateShouldFocusOnCheckoutError({ newShouldFocusOnCheckoutError: true });
    } else {
        // if could not map to any checkout error(s), show the generic error
        if (consoleCheckoutErrors.length === 0) {
            let genericError = {
                errorMessage: resources.genericErrorMessage,
                errorLocation: ErrorLocation.Checkout
            };

            await checkoutState.updateCheckoutError({ newCheckoutError: genericError });
            await checkoutState.updateShouldFocusOnCheckoutError({ newShouldFocusOnCheckoutError: true });
        }
    }
}

export async function focusOnCheckoutError(checkoutErrorRef: React.RefObject<HTMLElement>, ctx: IActionContext): Promise<void> {
    const checkoutState: ICheckoutState = await getCheckoutState(ctx).catch((error: Error) => {
        ctx.telemetry.exception(error);
        throw error;
    });

    checkoutErrorRef.current && checkoutErrorRef.current.scrollIntoView();

    await checkoutState.updateCheckoutErrorFocus({ newCheckoutErrorFocus: CheckoutModule.None });
    await checkoutState.updateShouldFocusOnCheckoutError({ newShouldFocusOnCheckoutError: false });
}

export async function setCheckoutErrorFocus(checkoutModuleId: string, ctx: IActionContext): Promise<void> {
    const checkoutState: ICheckoutState = await getCheckoutState(ctx).catch((error: Error) => {
        ctx.telemetry.exception(error);
        throw error;
    });

    for (let checkoutModule of Object.keys(CheckoutModule)) {
        if (checkoutModuleId.includes(CheckoutModule[checkoutModule])) {
            await checkoutState.updateCheckoutErrorFocus({ newCheckoutErrorFocus: CheckoutModule[checkoutModule] });
            return;
        }
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getCheckoutErrorMessage(resources: any, errorMessageResource: ErrorMessageResource): string {
    let errorResource = Object.keys(resources).find(resource => resource === errorMessageResource);
    return resources[errorResource!];
}
